import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mail, Send, GraduationCap, Github, Youtube, Linkedin, MessageSquare } from "lucide-react";

const FOUNDER = {
  linkedin: "https://in.linkedin.com/in/bharani-s-44a41b2a6",
  github: "https://github.com/bharanisivakumar6374-cmd",
  youtube: "https://www.youtube.com/watch?v=x09MECz3F5A",
  email: "bharani.sivakumar6374@gmail.com",
};

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we'll get back to you shortly.");
      setSent(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-[#0B1120] text-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center"><GraduationCap className="w-5 h-5" /></div>
            <div>
              <div className="font-display font-black text-lg leading-none">TrillionBytex<span className="text-blue-400">edu</span></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Smart TBX Learning Hub</div>
            </div>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/about" data-testid="contact-nav-about" className="text-slate-300 hover:text-white">About</Link>
            <Link to="/login" data-testid="contact-nav-signin" className="text-slate-300 hover:text-white">Sign in</Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-16 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-blue-600 mb-2">Contact</div>
          <h1 className="font-display font-black text-4xl text-slate-900 tracking-tight">Say hello.</h1>
          <p className="mt-2 text-slate-600 max-w-lg">Questions, partnership ideas, feedback, or something broken? Drop us a note and we&apos;ll get back to you.</p>

          {sent ? (
            <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-6" data-testid="contact-sent-panel">
              <div className="font-display font-bold text-lg text-emerald-800">Message received</div>
              <p className="text-sm text-emerald-700 mt-1">Thanks — we&apos;ll reply to <b>{form.email}</b> as soon as we can.</p>
              <div className="mt-4 flex gap-3">
                <button onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); }} className="px-4 py-2 rounded-full bg-white border border-emerald-300 text-emerald-700 text-sm">Send another</button>
                <Link to="/" className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm">Home</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Your name" required>
                  <input required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Email" required>
                  <input required type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                </Field>
              </div>
              <Field label="Subject" required>
                <input required data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Message" required>
                <textarea required data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full min-h-[120px] px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm" />
              </Field>
              <button disabled={loading} data-testid="contact-send-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                <Send className="w-4 h-4" /> {loading ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>

        <aside className="lg:col-span-2">
          <div className="bg-[#0B1120] text-white rounded-2xl p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-blue-400 mb-2">Reach out</div>
            <div className="font-display font-bold text-lg">Bharani Sivakumar</div>
            <div className="text-xs text-slate-400 mb-4">Founder & Editor · TrillionBytex</div>
            <div className="space-y-2 text-sm">
              <a href={`mailto:${FOUNDER.email}`} className="flex items-center gap-2 text-slate-300 hover:text-emerald-400"><Mail className="w-4 h-4" /> {FOUNDER.email}</a>
              <a href={FOUNDER.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-300 hover:text-blue-400"><Linkedin className="w-4 h-4" /> LinkedIn</a>
              <a href={FOUNDER.github} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-300 hover:text-violet-400"><Github className="w-4 h-4" /> GitHub</a>
              <a href={FOUNDER.youtube} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-300 hover:text-red-400"><Youtube className="w-4 h-4" /> YouTube</a>
            </div>
          </div>
          <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-6">
            <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center"><MessageSquare className="w-4 h-4" /></div>
            <div className="mt-2 font-display font-bold text-slate-900">Response time</div>
            <p className="text-xs text-slate-500 mt-1">Usually within 24 hours. Urgent? Ping via LinkedIn.</p>
          </div>
        </aside>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-12">
        <button onClick={() => nav(-1)} data-testid="contact-previous-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm hover:bg-slate-800">
          <ArrowLeft className="w-4 h-4" /> Previous Page
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";
function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label} {required && <span className="text-red-500">*</span>}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
